<template>
    <main class="inventory-page__content">
        <VInventoryHeader></VInventoryHeader>

        <VContentHeaderDefault
            title="Шаблоны"
            :button="$_canManageInventory ? 'Добавить шаблон' : ''"
            @search="onSearch"
            @button="onCreateButtonClick"
        ></VContentHeaderDefault>

        <template v-if="isLoading">
            <div class="inventory-page__loader">
                <VSpinner></VSpinner>
            </div>
        </template>

        <template v-else>
            <VContentTableDefault
                :rows="rows"
                :page="page"
                :total="total"
                :options="options"
                @page="onPageChange"
                @edit="onInventoryClick"
                @delete="onDeleteClick"
                @click="onInventoryClick"
            ></VContentTableDefault>
            <p class="inventory-page__empty" v-if="!rows.length">Шаблонов не найдено.</p>
        </template>

        <VInventoryPresetModal :cache="cache"></VInventoryPresetModal>
        <VInventoryAttributeModal :cache="cache"></VInventoryAttributeModal>
    </main>
</template>
<script>
// Components
import VSpinner from '../../components/VProgress/VSpinner.vue';
import PagePaginate from '../../components/PagePaginate/PagePaginate.vue';
import VContentTableDefault from '../../components/VContentTableDefault.vue';
import VContentHeaderDefault from '../../components/VContentHeaderDefault.vue';
import VInventoryHeader from './components/VInventoryHeader.vue';
import VInventoryPresetModal from './modals/VInventoryPresetModal.vue';
import VInventoryAttributeModal from './modals/VInventoryAttributeModal.vue';
import './InventoryPage.scss';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { errorMessage } from '../../../js/error';
import {
    paginateInventoryPresets,
    updateInventoryPreset,
    deleteInventoryPreset,
    deleteInventoryPresetPreview,
    getInventoryAttributeTypes,
    createInventoryPreset,
    getAllInventoryUsers,
    setInventoryPresetPreview,
    uploadFileToStorage,
} from '../../../js/inventory-api';
import { Watch } from 'vue-property-decorator';
import ModalNames from '../../../js/enums/ModalNames';

@Component({
    components: {
        VSpinner,
        PagePaginate,
        VContentTableDefault,
        VContentHeaderDefault,
        VInventoryHeader,
        VInventoryPresetModal,
        VInventoryAttributeModal,
    },
})
class InventoryPagePresets extends Vue {
    rows = [];
    page = 1;
    total = 1;
    isLoading = true;
    searchString = '';
    cache = {};

    /**
     * Колонки и действия строки.
     *
     * ВЫЧИСЛЯЕМОЕ, А НЕ ПОЛЕ КЛАССА: deletable зависит от права правки, а право живёт
     * в хранилище и приходит через миксин. Поле класса считается один раз при сборке
     * data — миксинов на том экземпляре ещё нет, и признак навсегда остался бы ложным.
     *
     * editable false намеренно: строка открывается по клику целиком, и «карандаш»
     * рядом делал бы то же самое. Удаление показывается только тому, кто вправе править
     * раздел; наблюдателю страница остаётся списком без действий.
     *
     * СУММА size НАМЕРЕННО МЕНЬШЕ СТА. Колонку действий VContentTableDefault рисует
     * из остатка (см. defaultColumnSize): при сумме ровно 100 остаток равен нулю, и она
     * не появляется вовсе — вместе с кнопкой удаления. Здесь оставлено 10 на действия.
     */
    get options() {
        return {
            editable: false,
            deletable: this.$_canManageInventory,
            columns: [
                {
                    size: 5,
                    label: '№',
                    display: (x) => x.id,
                },
                {
                    size: 40,
                    label: 'Имя',
                    display: (x) => x.preset_name,
                },
                {
                    size: 45,
                    label: 'Описание',
                    display: (x) => x.preset_description,
                },
            ],
        };
    }

    onSearch(searchString) {
        this.page = 1;
        this.searchString = searchString;
    }

    onPageChange(page) {
        this.page = page;
    }

    async onInventoryClick(preset) {
        // Без права правки строка не открывается: здесь нет режима просмотра,
        // и форма всё равно упала бы 403 при сохранении. Для наблюдателя страница
        // остаётся списком.
        if (!this.$_canManageInventory) {
            return;
        }

        try {
            const result = await this.$showModal(ModalNames.INVENTORY_PRESET_CARD, {
                preset: { ...preset, attributes: [...preset.attributes] },
            });

            this.isLoading = true;

            try {
                await updateInventoryPreset(result.preset);
                await this.applyPresetPhoto(result.preset.id, result.photo);
                await this.fetchInventoryPresets();
            } catch (error) {
                let message = errorMessage(error);
                this.$showModal(ModalNames.ERROR, { errorMessage: message });
            } finally {
                this.isLoading = false;
            }
        } catch (error) {
            // The modal was closed.
            return;
        }
    }

    /**
     * Применить намерение по типовому изображению модели.
     *
     * ОТДЕЛЬНЫМ ШАГОМ ПОСЛЕ СОХРАНЕНИЯ ШАБЛОНА, и иначе не выходит: у нового шаблона
     * номера ещё нет, а картинка адресуется именно им. Один порядок для нового и для
     * существующего — вместо двух правил, из которых одно объясняется словами
     * «а здесь пока нельзя».
     *
     * Ничего не выбрали и ничего не убирали — запроса не будет вовсе: сохранение
     * шаблона не должно трогать картинку, о которой человек не просил.
     *
     * Файл идёт в хранилище общим загрузчиком приложения, а в шаблон — уже загруженный
     * file_id. Два запроса вместо одного, зато один механизм записи в хранилище.
     *
     * @param {number} presetId
     * @param {{file: File|null, remove: boolean}} photo
     */
    async applyPresetPhoto(presetId, photo) {
        if (!presetId || !photo) {
            return;
        }

        if (photo.file) {
            const uploaded = await uploadFileToStorage(photo.file);
            const file = Array.isArray(uploaded.data) ? uploaded.data[0] : null;

            if (!file || !file.id) {
                throw new Error('Файл не загрузился. Попробуйте ещё раз.');
            }

            await setInventoryPresetPreview(presetId, file.id);

            return;
        }

        if (photo.remove) {
            await deleteInventoryPresetPreview(presetId);
        }
    }

    /**
     * Удалить шаблон.
     *
     * ПОДТВЕРЖДЕНИЕ ЗДЕСЬ ЕСТЬ, в отличие от снятия вложения: шаблон собирают руками из
     * десятка атрибутов, и восстановить его после случайного нажатия нечем — отмены в
     * разделе нет.
     *
     * Удалять можно без оглядки на созданное оборудование: шаблон копируется в предмет
     * при создании и дальше с ним не связан (см. InventoryPresetsController::delete).
     * Поэтому здесь нет проверки зависимостей, а у значений справочника и держателей есть.
     *
     * @param {Object} preset
     */
    onDeleteClick(preset) {
        if (!this.$_canManageInventory) {
            return;
        }

        this.$showModal(ModalNames.CONFIRM, {
            title: 'Удаление шаблона',
            text: `Удалить шаблон «${preset.preset_name}»? Уже созданное по нему оборудование останется на месте.`,
            cancelTitle: 'Отмена',
            confirmTitle: 'Удалить',
            confirmAction: () => this.deletePreset(preset),
        });
    }

    /**
     * @param {Object} preset
     */
    async deletePreset(preset) {
        this.isLoading = true;

        try {
            await deleteInventoryPreset(preset.id);
            await this.fetchInventoryPresets();
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        } finally {
            this.isLoading = false;
        }
    }

    async onCreateButtonClick() {
        try {
            const result = await this.$showModal(ModalNames.INVENTORY_PRESET_CARD, {
                // Шаблон — это название, описание и набор полей формы создания.
                // Полей закупки (цена, дата поставки, срок гарантии, ответственный)
                // здесь нет: это свойства партии, а не описание формы.
                preset: {
                    preset_name: '',
                    preset_description: '',
                    name: '',
                    attributes: [],
                },
            });

            this.isLoading = true;

            try {
                const created = await createInventoryPreset(result.preset);

                // Картинка ставится по номеру только что созданного шаблона — до
                // сохранения его не существовало, и поставить её было некуда.
                await this.applyPresetPhoto(created.data?.id, result.photo);
                await this.fetchInventoryPresets();
            } catch (error) {
                let message = errorMessage(error);

                this.$showModal(ModalNames.ERROR, { errorMessage: message });
            } finally {
                this.isLoading = false;
            }
        } catch (error) {
            // The modal was closed.
            return;
        }
    }

    @Watch('page')
    @Watch('searchString')
    async watcher() {
        this.isLoading = true;

        try {
            await this.fetchInventoryPresets();
        } finally {
            this.isLoading = false;
        }
    }

    async fetchRequiredData() {
        this.isLoading = true;

        try {
            await Promise.all([this.fetchInventoryPresets(), this.fetchAttributeTypes(), this.fetchUsers()]);
        } finally {
            this.isLoading = false;
        }
    }

    async fetchUsers() {
        try {
            const response = await getAllInventoryUsers(1, '', 100000);
            this.cache.users = response.data.data.map((user) => ({
                id: user.id,
                displayName: user.enabled ? user.displayName : user.displayName + ' (откл.)',
            }));
        } catch (error) {
            const message = errorMessage(error);

            this.$showModal(ModalNames.ERROR, { errorMessage: message });
        }
    }

    async fetchInventoryPresets(query = {}) {
        try {
            const response = await paginateInventoryPresets(this.page, this.searchString, 15, query);

            this.rows = response.data.data;
            this.total = response.data.total;
        } catch (error) {
            const message = errorMessage(error);

            this.$showModal(ModalNames.ERROR, { errorMessage: message });
        }
    }

    async fetchAttributeTypes() {
        try {
            const result = await getInventoryAttributeTypes({
                withAttributes: true,
            });

            this.cache.attributes = result.data.data;
            this.cache.attributesMap = this.cache.attributes.reduce((carry, attributeType) => {
                carry[attributeType.type] = attributeType;
                return carry;
            }, {});
        } catch (error) {
            const message = errorMessage(error);

            this.$showModal(ModalNames.ERROR, { errorMessage: message });
        }
    }

    created() {
        this.fetchRequiredData();
    }
}

export default InventoryPagePresets;
</script>
<style lang="scss"></style>
